import { useRef, useLayoutEffect, useEffect } from "react";
import { initSCORM, teardownSCORM } from "../../../core/services/scorm-api";
import axiosService from "../../../core/services/axios-service";
import { ApiRoutes } from "../../../core/dictionary/api-url";
import ToastService from "../../../core/services/toaster-service";

/**
 * CourseViewer — pure SCORM iframe player.
 *
 * window.API must exist before the iframe content's JS runs. useLayoutEffect
 * fires synchronously after the DOM commit and before browser paint, which
 * means it always runs before any async iframe network fetch can complete —
 * safe for SCORM init. It also re-runs on React Strict Mode's cleanup+remount
 * cycle, preventing the window.API deletion that the old render-phase guard
 * could not recover from.
 *
 * Use `key={courseId}` on this component when switching modules so React
 * unmounts the old instance (teardownSCORM) and mounts the new one (initSCORM)
 * in the right order. The session guard in scorm-api ensures a stale teardown
 * never deletes a newer module's window.API.
 */
export default function CourseViewer({ courseId, courseUrl, courseTitle = "Course", paused = false, onProgressChange, onSlideChange }) {
  // ── Full SCORM commit (LMSCommit / LMSFinish) ─────────────────────────────
  const onCommitRef = useRef(null);
  onCommitRef.current = async (payload) => {
    onProgressChange?.(payload.coursePercentage, payload.status, true);
    try {
      await axiosService.post(ApiRoutes.UPDATE_COURSE_PROGRESS, payload);
    } catch {
      ToastService.error("Failed to save progress. Please check your connection.");
    } finally {
      onProgressChange?.(payload.coursePercentage, payload.status, false);
    }
  };

  // ── Slide navigation (lesson_location / cmi.location change) ─────────────
  // Always reads the latest onSlideChange without needing it in useLayoutEffect deps.
  const onSlideChangeRef = useRef(null);
  onSlideChangeRef.current = onSlideChange;

  const sessionRef = useRef(null);
  const iframeRef = useRef(null);
  // Tracks where we are inside the current module: { number, location }.
  // `number` increments on every slide (lesson_location) change.
  const slideTrackRef = useRef({ number: 0, location: "" });

  // ── Req 1: auto-advance to the next slide once the current one completes ────
  //
  // Articulate drives slide navigation INSIDE the iframe — the SCORM/LMS layer
  // cannot push it forward through the standard API. Because this package is
  // served same-origin (from /public), we can reach into the iframe document and
  // treat the current slide's media `ended` event as its "completion" signal,
  // then drive the Storyline player to the next slide. This is best-effort:
  // it degrades gracefully (and logs) if the player API isn't reachable.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !courseUrl) return;

    let pollId = null;
    const boundMedia = new Set();
    let lastAdvancedAt = 0;
    let currentSlideId = null;

    // Drive the slide forward via the Storyline player, with fallbacks.
    const tryAdvanceSlide = () => {
      // Debounce: ignore overlapping media-ended bursts on the same slide.
      const now = Date.now();
      if (now - lastAdvancedAt < 1200) return;
      lastAdvancedAt = now;

      const { number, location } = slideTrackRef.current;
      
      try {
        const win = iframe.contentWindow;
        if (!win) return;

        // 1) Storyline player API (some builds expose a navigation method).
        if (typeof win.GetPlayer === "function") {
          const player = win.GetPlayer();
          if (player && typeof player.next === "function") {
            player.next();
            return;
          }
        }
        // 2) Click the built-in Next control. Storyline 360 renders it as
        //    <button id="next" class="cs-button" aria-label="Next …">, so map
        //    the id directly and fall back to broader selectors for other builds.
        const doc = win.document;
        const nextBtn =
          (doc && doc.getElementById("next")) ||
          (doc && doc.querySelector(
            'button.cs-button[aria-label^="Next" i],[data-name="next"],[data-control-id="next"],.cs-next,.next-slide'
          ));
        if (nextBtn && nextBtn.getAttribute("aria-disabled") !== "true") {
          nextBtn.click();
          return;
        }
        // 3) Keyboard fallback — this build maps "Next" to Ctrl+Alt+Period
        //    (per the button's aria-label).
        win.document.dispatchEvent(new win.KeyboardEvent("keydown", {
          key: ".", code: "Period", keyCode: 190, which: 190,
          ctrlKey: true, altKey: true, bubbles: true,
        }));
      } catch (err) {
        console.warn(
          "[slide-autoadvance] iframe player not reachable — slide auto-advance " +
          "must be enabled in the Articulate publish settings:", err?.message
        );
      }
    };

    const onMediaEnded = () => {
      tryAdvanceSlide();
    };

    // Read the id of the slide currently on stage. Storyline renders each slide
    // in a `.slide-container[data-slide-id]`; during a transition two may exist,
    // so we prefer the visible one and fall back to the last in the DOM.
    const readActiveSlideId = (doc) => {
      const containers = doc.querySelectorAll(
        ".slide-container[data-slide-id], .slide-container[data-model-id]"
      );
      if (!containers.length) return null;
      let active = null;
      containers.forEach((c) => { if (c.offsetParent !== null) active = c; });
      active = active || containers[containers.length - 1];
      return active.getAttribute("data-slide-id") || active.getAttribute("data-model-id");
    };

    // Attach `ended` once per media element. Storyline REUSES the same
    // <audio>/<video> element across slides and just swaps its src, so a single
    // listener keeps firing as each slide's narration finishes.
    const bindMedia = (doc) => {
      doc.querySelectorAll("video, audio").forEach((el) => {
        if (boundMedia.has(el) || el.loop) return;
        boundMedia.add(el);
        el.addEventListener("ended", onMediaEnded);
      });
    };

    // Poll the slide stage: detect when the active slide changes (drives the
    // counter + "new slide started" log) and keep media listeners attached.
    // This build stores position in suspend_data, not lesson_location, so the
    // SCORM onLocationChange hook never fires — the DOM is our only slide signal.
    const tick = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        const id = readActiveSlideId(doc);
        if (id && id !== currentSlideId) {
          currentSlideId = id;
          slideTrackRef.current = {
            number: slideTrackRef.current.number + 1,
            location: id,
          };
        }
        bindMedia(doc);
      } catch {
        // cross-origin / not ready
      }
    };

    const onLoad = () => {
      tick();
      pollId = setInterval(tick, 1000);
    };

    iframe.addEventListener("load", onLoad);
    return () => {
      iframe.removeEventListener("load", onLoad);
      if (pollId) clearInterval(pollId);
      boundMedia.forEach((el) => {
        try { el.removeEventListener("ended", onMediaEnded); } catch { /* gone */ }
      });
      boundMedia.clear();
    };
  }, [courseUrl, courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pause the module in the background ─────────────────────────────────────
  // When `paused` is true (e.g. after "Mark as Completed"), stop any narration
  // /video playing inside the Storyline iframe. The package is served same-origin
  // so we can reach in and pause its media. Storyline can resume media on its own
  // (slide transitions, autoplay), so we keep re-pausing on a light interval.
  useEffect(() => {
    if (!paused) return;
    const iframe = iframeRef.current;
    if (!iframe) return;

    const pauseMedia = () => {
      try {
        const win = iframe.contentWindow;
        const doc = iframe.contentDocument;
        if (win && typeof win.GetPlayer === "function") {
          const player = win.GetPlayer();
          if (player && typeof player.pause === "function") player.pause();
        }
        if (doc) {
          doc.querySelectorAll("video, audio").forEach((el) => {
            try { el.pause(); } catch { /* ignore */ }
          });
        }
      } catch { /* cross-origin / not ready */ }
    };

    pauseMedia();
    const id = setInterval(pauseMedia, 500);
    return () => clearInterval(id);
  }, [paused]);

  useLayoutEffect(() => {
    sessionRef.current = initSCORM({
      courseId,
      onCommit:         (payload)  => { onCommitRef.current(payload);},
      onLocationChange: (location) => {
        if (typeof onSlideChangeRef.current === 'function') {
          onSlideChangeRef.current(location);
        } else {
          console.warn('[CourseViewer] onSlideChangeRef.current is not a function:', onSlideChangeRef.current);
        }
      },
    });
    return () => teardownSCORM(sessionRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="course-viewer">
      {courseUrl ? (
        <iframe
          ref={iframeRef}
          src={courseUrl}
          title={courseTitle}
          className="course-viewer__frame"
          allowFullScreen
          allow="fullscreen"
        />
      ) : (
        <div className="course-viewer__placeholder">
          <p>Course URL is not configured yet.</p>
        </div>
      )}
    </div>
  );
}
